// src/pages/EquiposPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/ui/Navbar";
import EquiposForm from "../components/ui/EquiposForm";
import axios from "axios";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import {
  LoaderPinwheel,
  Cpu,
  Download,
  Plus,
  Search,
  X,
  Pencil,
  Trash2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
} from "lucide-react";
import Switch from "../components/ui/Switch";
import SelectField from "../components/ui/SelectField";
import { hasPermission } from "../utils/permissions";
import ModalInventarioDescarga from "../components/modals/ModalInventarioDescarga";
import PageSizeSelect from "../components/ui/PageSizeSelect";

const CONDICION_BADGE = {
  Bueno: "bg-green-50 text-green-700 border border-green-200",
  Regular: "bg-amber-50 text-amber-700 border border-amber-200",
  Malo: "bg-red-50 text-red-700 border border-red-200",
  "Fuera de servicio": "bg-gray-100 text-gray-500 border border-gray-200",
};

const EquiposPage = () => {
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [filterCliente, setFilterCliente] = useState("");
  const [filterSede, setFilterSede] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("");
  const [filterSedeGrupo, setFilterSedeGrupo] = useState("");
  const [sedeGrupos, setSedeGrupos] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [loadingSedes, setLoadingSedes] = useState(false);
  const [filtrosExtra, setFiltrosExtra] = useState({});
  const [caracteristicas, setCaracteristicas] = useState([]);
  const [responsables, setResponsables] = useState([]);

  const [clientes, setClientes] = useState([]);
  const [categorias, setCategorias] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;

  const fetchFiltros = async () => {
    const res = await axios.get(`${API}/equipos/filtros`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setCaracteristicas(res.data.caracteristicas);
    setResponsables(res.data.responsables);
  };

  const fetchSelects = async () => {
    try {
      const [cRes, catRes, gRes] = await Promise.all([
        axios.get(`${API}/clientes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/cat`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/sede-grupos`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setClientes(cRes.data);
      setCategorias(catRes.data);
      setSedeGrupos(gRes.data || []);
    } catch (err) {
      toast.error("No se pudieron cargar clientes/categorías");
    }
  };

  const fetchSedesByCliente = async (clienteId) => {
    if (!clienteId) {
      setSedes([]);
      return;
    }
    setLoadingSedes(true);
    try {
      const res = await axios.get(`${API}/sedes/${clienteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSedes(res.data || []);
    } catch {
      setSedes([]);
    } finally {
      setLoadingSedes(false);
    }
  };

  const fetchEquipos = async (opts = {}) => {
    setLoading(true);
    try {
      const sedeGrupoActual =
        opts.sedeGrupo !== undefined ? opts.sedeGrupo : filterSedeGrupo;
      const sedeActual = opts.sede !== undefined ? opts.sede : filterSede;
      const params = {
        view: "list",
        search: opts.search ?? search ?? undefined,
        cliente: sedeGrupoActual
          ? undefined
          : (opts.cliente ?? filterCliente ?? undefined),
        sede: sedeGrupoActual ? undefined : sedeActual || undefined,
        categoria: opts.categoria ?? filterCategoria ?? undefined,
        sedeGrupo: sedeGrupoActual || undefined,
        ...filtrosExtra,
      };
      Object.keys(params).forEach(
        (k) => params[k] === undefined && delete params[k],
      );
      const res = await axios.get(`${API}/equipos`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setEquipos(res.data);
      setPage(1);
    } catch (err) {
      toast.error("Error al cargar equipos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSelects();
    fetchEquipos();
    fetchFiltros();
    // eslint-disable-next-line
  }, []);

  const handleSearch = () => fetchEquipos({ search });
  const handleFilterChange = (key, value) => {
    if (key === "cliente") {
      setFilterCliente(value);
      setFilterSede("");
      fetchSedesByCliente(value);
    }
    if (key === "sede") setFilterSede(value);
    if (key === "categoria") setFilterCategoria(value);
    fetchEquipos({
      cliente: key === "cliente" ? value : undefined,
      sede: key === "sede" ? value : key === "cliente" ? "" : undefined,
      categoria: key === "categoria" ? value : undefined,
    });
  };

  const handleCreate = () => {
    setEditing(null);
    setShowForm(true);
  };
  const handleEdit = (eq) => {
    setEditing(eq);
    setShowForm(true);
  };
  const handleDelete = async (eq) => {
    if (
      !window.confirm(
        "¿Eliminar este equipo y todos sus registros relacionados?",
      )
    )
      return;
    const codigoIngresado = window
      .prompt(`Para confirmar, escribe el código del equipo (${eq.codigo}).`)
      ?.trim();
    if (!codigoIngresado) return;
    if (codigoIngresado !== eq.codigo) {
      toast.error("El código no coincide. No se eliminó el equipo.");
      return;
    }
    try {
      await axios.delete(`${API}/equipos/${eq._id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { codigoConfirmacion: codigoIngresado },
      });
      toast.info("Equipo eliminado");
      fetchEquipos();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al eliminar equipo");
    }
  };

  const handleSave = async (payload) => {
    try {
      if (editing) {
        await axios.put(`${API}/equipos/${editing._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Equipo actualizado");
      } else {
        await axios.post(`${API}/equipos`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Equipo creado");
      }
      setShowForm(false);
      setEditing(null);
      fetchEquipos();
    } catch (err) {
      toast.error("Error al guardar equipo");
      throw err;
    }
  };

  const handleClearFilters = () => {
    setSearch("");
    setFilterCliente("");
    setFilterSede("");
    setFilterCategoria("");
    setFilterSedeGrupo("");
    setSedes([]);
    setFiltrosExtra({});
    setPage(1);
    fetchEquipos({
      search: undefined,
      cliente: undefined,
      sede: undefined,
      categoria: undefined,
      sedeGrupo: undefined,
    });
  };

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return equipos.slice(start, start + pageSize);
  }, [equipos, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(equipos.length / pageSize));

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1);
  };

  const handleAutoCronogramaMasivo = async (autoCronograma, soloFiltrados) => {
    try {
      await axios.put(
        `${API}/equipos/autocronograma`,
        {
          autoCronograma,
          filtros: soloFiltrados
            ? {
                cliente: filterCliente || null,
                categoria: filterCategoria || null,
              }
            : {},
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        autoCronograma
          ? "Auto cronograma activado"
          : "Auto cronograma desactivado",
      );
      fetchEquipos();
    } catch (err) {
      toast.error("Error actualizando auto cronograma");
    }
  };

  const columnasExtra = useMemo(() => {
    const cols = [];
    if (filtrosExtra.responsable)
      cols.push({ key: "RESPONSABLE", label: "Responsable actual" });
    Object.keys(filtrosExtra).forEach((k) => {
      if (k.startsWith("caracteristicas.")) {
        const key = k.split(".")[1];
        const meta = caracteristicas.find((c) => c.key === key);
        if (meta) cols.push({ key, label: meta.nombre });
      }
    });
    return cols;
  }, [filtrosExtra, caracteristicas]);

  const hasActiveFilters =
    search ||
    filterCliente ||
    filterSede ||
    filterCategoria ||
    filterSedeGrupo ||
    Object.keys(filtrosExtra).length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-xl">
              <Cpu className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Equipos</h1>
              <p className="text-sm text-gray-500">
                {equipos.length} equipo{equipos.length !== 1 ? "s" : ""}{" "}
                registrado{equipos.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowInventarioModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              Descargar inventario
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nuevo equipo
            </button>
          </div>
        </div>

        {loading && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando datos, por favor espera...
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            {/* Buscar */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Serie o código…"
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
            </div>

            {/* Grupo de sedes */}
            {sedeGrupos.length > 0 && (
              <SelectField
                label="Grupo de sedes"
                name="sedeGrupo"
                value={filterSedeGrupo}
                options={sedeGrupos}
                placeholder="Todos los grupos"
                onChange={(e) => {
                  setFilterSedeGrupo(e.target.value);
                  if (e.target.value) {
                    setFilterCliente("");
                    setFilterSede("");
                    setSedes([]);
                  }
                  fetchEquipos({
                    sedeGrupo: e.target.value,
                    cliente: e.target.value ? undefined : filterCliente,
                    sede: undefined,
                  });
                }}
              />
            )}

            {/* Cliente */}
            <SelectField
              label="Cliente"
              name="cliente"
              value={filterCliente}
              options={clientes}
              placeholder="Todos los clientes"
              isDisabled={Boolean(filterSedeGrupo)}
              onChange={(e) => {
                setFilterSedeGrupo("");
                handleFilterChange("cliente", e.target.value);
              }}
            />

            {/* Sede */}
            <SelectField
              label="Sede"
              name="sede"
              value={filterSede}
              options={sedes}
              placeholder={loadingSedes ? "Cargando..." : "Todas las sedes"}
              isDisabled={
                Boolean(filterSedeGrupo) || !filterCliente || loadingSedes
              }
              onChange={(e) => handleFilterChange("sede", e.target.value)}
            />

            {/* Categoría */}
            <SelectField
              label="Categoría"
              name="categoria"
              value={filterCategoria}
              options={categorias}
              placeholder="Todas las categorías"
              onChange={(e) => handleFilterChange("categoria", e.target.value)}
            />
          </div>

          {/* Filtros activos */}
          {hasActiveFilters && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full font-medium">
                Filtros activos · {equipos.length} resultado
                {equipos.length !== 1 ? "s" : ""}
              </span>
              <button
                onClick={handleClearFilters}
                className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1"
              >
                <X className="h-3 w-3" /> Limpiar
              </button>
            </div>
          )}

          {/* Filtros avanzados */}
          <details className="mt-4">
            <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1.5 w-fit">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filtros avanzados
            </summary>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Responsable */}
              <SelectField
                label="Responsable"
                name="responsable"
                value={filtrosExtra.responsable || ""}
                options={responsables.map((r) => ({ _id: r, name: r }))}
                placeholder="Todos"
                onChange={(e) =>
                  setFiltrosExtra((f) => ({
                    ...f,
                    responsable: e.target.value || undefined,
                  }))
                }
              />

              {/* Características dinámicas */}
              {caracteristicas.map((c) => (
                <div key={c.key}>
                  {c.tipo === "select" && (
                    <SelectField
                      label={c.nombre}
                      name={`caracteristicas.${c.key}`}
                      value={filtrosExtra[`caracteristicas.${c.key}`] ?? ""}
                      options={c.opciones.map((op) => ({ _id: op, name: op }))}
                      placeholder="Todos"
                      onChange={(e) =>
                        setFiltrosExtra((f) => ({
                          ...f,
                          [`caracteristicas.${c.key}`]:
                            e.target.value || undefined,
                        }))
                      }
                    />
                  )}
                  {c.tipo === "boolean" && (
                    <SelectField
                      label={c.nombre}
                      name={`caracteristicas.${c.key}`}
                      value={
                        filtrosExtra[`caracteristicas.${c.key}`] === undefined
                          ? ""
                          : String(filtrosExtra[`caracteristicas.${c.key}`])
                      }
                      options={[
                        { _id: "true", name: "Sí" },
                        { _id: "false", name: "No" },
                      ]}
                      placeholder="Todos"
                      onChange={(e) =>
                        setFiltrosExtra((f) => ({
                          ...f,
                          [`caracteristicas.${c.key}`]:
                            e.target.value === ""
                              ? undefined
                              : e.target.value === "true",
                        }))
                      }
                    />
                  )}
                  {["text", "number", "date"].includes(c.tipo) && (
                    <>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        {c.nombre}
                      </label>
                      <input
                        type={c.tipo}
                        value={filtrosExtra[`caracteristicas.${c.key}`] ?? ""}
                        onChange={(e) =>
                          setFiltrosExtra((f) => ({
                            ...f,
                            [`caracteristicas.${c.key}`]:
                              e.target.value || undefined,
                          }))
                        }
                        className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                      />
                    </>
                  )}
                </div>
              ))}

              {/* Acciones avanzadas */}
              <div className="flex gap-2 items-end">
                <button
                  onClick={() => fetchEquipos()}
                  className="flex-1 px-3 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Aplicar
                </button>
                <button
                  onClick={handleClearFilters}
                  className="flex-1 px-3 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Limpiar
                </button>
              </div>
            </div>
          </details>
        </div>

        {/* Auto cronograma */}
        {hasPermission("equipos.editar") && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                Auto cronograma masivo
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-1 bg-gray-50 border rounded-lg p-1">
                <button
                  onClick={() => handleAutoCronogramaMasivo(true, false)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md bg-green-500 text-white hover:bg-green-600 transition-colors"
                >
                  Activar todos
                </button>
                <button
                  onClick={() => handleAutoCronogramaMasivo(false, false)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  Desactivar todos
                </button>
              </div>
              <div className="flex items-center gap-1 bg-gray-50 border rounded-lg p-1">
                <button
                  onClick={() => handleAutoCronogramaMasivo(true, true)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                >
                  Activar filtrados
                </button>
                <button
                  onClick={() => handleAutoCronogramaMasivo(false, true)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                >
                  Desactivar filtrados
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabla */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
          <div className="flex items-center justify-end px-4 py-2 border-b border-gray-100">
            <PageSizeSelect value={pageSize} onChange={handlePageSizeChange} />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
                  <th className="py-3 px-4 text-left">Código</th>
                  <th className="py-3 px-4 text-left">Serial</th>
                  <th className="py-3 px-4 text-left">Cliente</th>
                  <th className="py-3 px-4 text-left">Sede</th>
                  <th className="py-3 px-4 text-left">Área</th>
                  <th className="py-3 px-4 text-left">Categoría</th>
                  {columnasExtra.map((col) => (
                    <th key={col.key} className="py-3 px-4 text-left">
                      {col.label}
                    </th>
                  ))}
                  <th className="py-3 px-4 text-left">Marca / Modelo</th>
                  <th className="py-3 px-4 text-left">Propiedad</th>
                  <th className="py-3 px-4 text-left">Condición</th>
                  <th className="py-3 px-4 text-left">Garantía</th>
                  <th className="py-3 px-4 text-center">Auto</th>
                  <th className="py-3 px-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan={13 + columnasExtra.length}
                      className="py-12 text-center"
                    >
                      <LoaderPinwheel className="h-6 w-6 animate-spin text-blue-400 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm">
                        Cargando equipos...
                      </p>
                    </td>
                  </tr>
                ) : paged.length === 0 ? (
                  <tr>
                    <td
                      colSpan={13 + columnasExtra.length}
                      className="py-16 text-center"
                    >
                      <Cpu className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                      <p className="text-gray-500 font-medium">
                        No se encontraron equipos
                      </p>
                      <p className="text-gray-400 text-xs mt-1">
                        Intenta ajustar los filtros o crea un nuevo equipo
                      </p>
                    </td>
                  </tr>
                ) : (
                  paged.map((eq) => (
                    <tr
                      key={eq._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-2.5 px-4">
                        <Link
                          to={`/equipos/${eq._id}`}
                          className="text-blue-600 hover:underline font-mono text-xs font-semibold"
                        >
                          {eq.codigo}
                        </Link>
                      </td>
                      <td className="py-2.5 px-4 font-mono text-xs text-gray-600">
                        {eq.serial}
                      </td>
                      <td className="py-2.5 px-4 text-gray-700">
                        {eq.cliente?.name || "—"}
                      </td>
                      <td className="py-2.5 px-4 text-gray-600">
                        {eq.sede?.name || "—"}
                      </td>
                      <td className="py-2.5 px-4 text-gray-600">
                        {eq.area?.name || "—"}
                      </td>
                      <td className="py-2.5 px-4">
                        {eq.categoria?.name ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                            {eq.categoria.name}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      {columnasExtra.map((col) => (
                        <td key={col.key} className="py-2.5 px-4 text-gray-600">
                          {col.key === "RESPONSABLE"
                            ? eq.responsableActual || "—"
                            : (eq.caracteristicas?.[col.key] ?? "—")}
                        </td>
                      ))}
                      <td className="py-2.5 px-4 text-gray-700">
                        <span className="font-medium">
                          {eq.marca?.name || "—"}
                        </span>
                        <span className="text-gray-400"> / </span>
                        <span>{eq.modelo?.name || "—"}</span>
                      </td>
                      <td className="py-2.5 px-4 text-gray-600 text-xs">
                        {eq.propiedad || "—"}
                      </td>
                      <td className="py-2.5 px-4">
                        {eq.condicion ? (
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${CONDICION_BADGE[eq.condicion] || "bg-gray-100 text-gray-600 border border-gray-200"}`}
                          >
                            {eq.condicion}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-gray-600 text-xs">
                        {eq.garantia || "—"}
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        {hasPermission("equipos.editar") && (
                          <Switch
                            checked={eq.autoCronograma}
                            onChange={async (value) => {
                              try {
                                await axios.put(
                                  `${API}/equipos/autocronograma`,
                                  {
                                    equipoIds: [eq._id],
                                    autoCronograma: value,
                                  },
                                  {
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                    },
                                  },
                                );
                                fetchEquipos();
                              } catch {
                                toast.error("Error actualizando equipo");
                              }
                            }}
                          />
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {hasPermission("equipos.editar") && (
                            <button
                              onClick={() => handleEdit(eq)}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                              <Pencil className="h-3 w-3" /> Editar
                            </button>
                          )}
                          {hasPermission("equipos.eliminar") && (
                            <button
                              onClick={() => handleDelete(eq)}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-red-50 border border-red-200 rounded-lg text-red-600 hover:bg-red-100 transition-colors"
                            >
                              <Trash2 className="h-3 w-3" /> Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginación */}
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span className="text-xs text-gray-400">
            {equipos.length === 0
              ? "0"
              : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, equipos.length)}`}{" "}
            de {equipos.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Anterior
            </button>
            <span className="px-2.5 py-1 bg-gray-100 rounded-md text-xs font-medium">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              Siguiente <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {showForm && (
        <EquiposForm
          equipo={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}

      {showInventarioModal && (
        <ModalInventarioDescarga
          onClose={() => setShowInventarioModal(false)}
        />
      )}
    </div>
  );
};

export default EquiposPage;
